// frontend/src/pages/website/blog/HowToSetUpThePerfectTradespersonProfile.jsx
import React from "react";
import BlogPostLayout from "./BlogPostLayout";

export default function HowToSetUpThePerfectTradespersonProfile() {
    return (
        <BlogPostLayout
            slug="how-to-set-up-the-perfect-tradesperson-profile"
            title="How to Set Up the Perfect Tradesperson Profile"
            metaTitle="How to Set Up the Perfect Tradesperson Profile | KonarCard"
            description="A practical guide for UK tradespeople on building a digital profile that wins jobs, what to write, what photos to take and how to make every section work for you."
            pillLabel="Tips & guides"
            datePublished="2026-04-13"
            readTime="10 min read"
        >
            <p>
                A digital profile is only as good as what you put into it. A half-finished profile with a phone number and no photos is better than nothing, but not by much. The tradespeople getting the most enquiries from their KonarCard profile are the ones who have taken thirty minutes to fill every section properly.
            </p>

            <p>
                This guide walks through every part of your profile and tells you exactly what to put in each one. Not in vague terms, specifically. What to write, what photos to take, how to describe your services and what to do if you are not sure where to start.
            </p>

            <p>
                By the end of this post your profile will be doing the job it is supposed to do, convincing a potential customer that you are the right person for their job before they have even called you.
            </p>

            <h2>Why Your Profile Matters More Than You Think</h2>

            <p>
                When a customer receives your card and taps it, or scans the QR code, they are doing something specific. They are deciding whether to trust you.
            </p>

            <p>
                That decision happens fast. Most people form an impression of a profile within a few seconds of it loading. If they see a professional-looking page with photos of real work, genuine reviews and clear service information, they feel reassured. If they see an empty profile with just a name and number, they feel uncertain, and uncertain customers do not book.
            </p>

            <p>
                Your profile is your first impression for every customer who taps your card. It is worth getting right.
            </p>

            <h2>Your Profile Photo and Cover Image</h2>

            <p>
                The first thing a customer sees when your profile loads is your cover image and your profile photo. These two images set the tone for everything that follows.
            </p>

            <p>
                <strong>Your profile photo</strong> should be a clear photo of your face. Not a logo, not a van, not a cartoon. A real photo of you, ideally in your work gear, looking directly at the camera. Customers are making a trust decision and they want to see the person they are inviting into their home. A clear, friendly photo of your face is worth more than any amount of professional branding.
            </p>

            <p>
                You do not need a professional photographer. A photo taken by someone else on a modern smartphone in good natural light is enough. Stand near a window or outside on an overcast day, harsh direct sunlight creates unflattering shadows. Make sure the background is reasonably tidy and not distracting.
            </p>

            <p>
                <strong>Your cover image</strong> is the wide banner at the top of your profile. This is a good place for a photo of your work, a finished kitchen, a completed garden, a tiled bathroom, whatever best represents the quality of what you do. If you have a van with your branding on it, a clean shot of that works well too.
            </p>

            <p>
                Avoid using stock images for either of these. Customers can tell the difference and stock images undermine exactly the trust you are trying to build.
            </p>

            <h2>Your Business Name and Trade Title</h2>

            <p>
                Your business name should be whatever customers know you as. If you trade as a sole trader under your own name, use that. If you have a company name, use that. Keep it consistent with how you appear on any other platform, Google, Checkatrade, MyBuilder or wherever else customers might find you.
            </p>

            <p>
                Your trade title sits below your name and tells customers immediately what you do. Keep it simple and specific. Not "multi-skilled professional" or "property services specialist", those phrases mean nothing to a customer who needs a specific job done. Use the trade name customers actually search for.
            </p>

            <p>Good examples:</p>
            <ul>
                <li>Electrician</li>
                <li>Plumber</li>
                <li>Builder</li>
                <li>Roofer</li>
                <li>Gas Engineer</li>
                <li>Decorator</li>
                <li>Landscaper</li>
                <li>Handyman</li>
            </ul>

            <p>
                If you cover more than one trade, list your primary trade first and add the others. Keep it to two or three at most. A profile that claims to do everything often feels less credible than one that is clearly focused.
            </p>

            <h2>Your Location and Service Area</h2>

            <p>
                Be specific about where you work. Customers want to know you cover their area before they look at anything else on your profile.
            </p>

            <p>
                List your base location and the areas you cover. If you work across a county or a group of towns, say so. If you only cover a specific postcode area, say that instead. Clarity here saves time for you and for the customer, you avoid enquiries from people who are too far away and customers in your area know immediately that you are relevant to them.
            </p>

            <h2>Your Services Section</h2>

            <p>
                The services section is where most tradespeople either do very well or miss a significant opportunity. Done properly it answers the customer's first question, can this person do what I need, without them having to ask.
            </p>

            <p>
                List every service you offer that is relevant to your main trade. Be specific rather than general. Instead of "electrical work" list the individual services: consumer unit upgrades, EV charger installation, socket installation, lighting installation, rewires, fault finding, PAT testing.
            </p>

            <p>
                A specific service list does two things. It tells customers exactly what you offer and it reassures them that you are experienced in their specific job rather than a generalist who will figure it out when they get there.
            </p>

            <p>
                <strong>Adding prices where possible</strong> is one of the most effective things you can do. Not every trade can give fixed prices, a builder quoting on an extension cannot give a number without seeing the job, but many services do have a standard rate. A call-out fee, an hourly rate, a fixed price for a consumer unit upgrade or a bathroom installation. Publishing these prices filters out customers who cannot afford your rates, which saves time, and it signals confidence, tradespeople who are good at what they do and know their worth are not afraid to publish their prices.
            </p>

            <p>
                If you genuinely cannot give a fixed price, a range is better than nothing. "From £X" or "typically £X to £Y depending on the size of the job" gives customers a realistic expectation without committing you to a number before you have seen the work.
            </p>

            <h2>Your Work Photos</h2>

            <p>
                Work photos are the most persuasive part of your profile. More persuasive than your description. More persuasive than your prices. For most trades the quality of the finish is the main thing the customer is buying and photos are the only way to demonstrate that quality before the job starts.
            </p>

            <p>
                <strong>Take photos of every job you complete.</strong> Before you pack up, take five minutes to photograph the finished work. Take multiple shots from different angles. If you have a before photo, even an informal one taken on your phone when you arrived, use it. Before and after photos are significantly more compelling than after-only shots because they show the transformation.
            </p>

            <p><strong>What makes a good work photo:</strong></p>
            <ul>
                <li>Clear and well lit, open a window, turn on the lights, avoid dark corners.</li>
                <li>The subject fills most of the frame, do not stand so far back that the work is a small part of a large photo.</li>
                <li>The area around the work is tidy, move any mess, tools or materials out of shot before you take the photo.</li>
                <li>Taken in landscape orientation for most profile templates.</li>
            </ul>

            <p><strong>What to photograph by trade:</strong></p>

            <p>
                <strong>Electricians</strong>, consumer units, finished socket installations, light fittings, EV chargers, fuse board upgrades. Show the neatness of the wiring and the quality of the finish.
            </p>

            <p>
                <strong>Plumbers</strong>, completed bathroom installations, boiler replacements, underfloor heating, neat pipework. Customers hiring plumbers for bathrooms want to see previous bathroom work specifically.
            </p>

            <p>
                <strong>Builders</strong>, completed extensions, loft conversions, new builds. Before and after shots showing the scale of the transformation are particularly effective.
            </p>

            <p>
                <strong>Decorators</strong>, finished rooms showing clean lines, neat edges and good colour choices. Take photos in daylight with the lights on for the truest colours.
            </p>

            <p>
                <strong>Roofers</strong>, completed roof replacements, new felt and batten work, finished ridge tiles, lead flashing. Customers cannot easily assess roofing quality themselves so detailed photos of neat, professional work carry significant weight.
            </p>

            <p>
                <strong>Landscapers</strong>, completed garden transformations are among the most shareable and engaging photos on any profile. Wide shots of the finished garden alongside close-up details of features like patios, planting and water features work well.
            </p>

            <p>
                Aim for at least six photos on your profile before you start sharing it. More is better, a profile with twelve varied photos covering several job types is significantly more credible than one with two or three.
            </p>

            <h2>Your Contact Details</h2>

            <p>
                Fill in every contact field that is relevant to you. Phone number and email at minimum. If you use WhatsApp for customer communication, include that. If you have a Facebook business page or an Instagram where you post your work, link those too.
            </p>

            <p>
                The goal is to make it as easy as possible for a customer to reach you in whatever way they prefer. Some customers will call. Some will send a message. Some will email. Remove every possible barrier to contact.
            </p>

            <h2>Your Social Links</h2>

            <p>
                If you post your work on social media, linking those accounts from your profile extends the amount of work a customer can see before they make a decision. An Instagram account with fifty photos of completed jobs is a powerful addition to a profile that already shows six to twelve photos.
            </p>

            <p>
                If you do not post on social media regularly, do not link accounts that are empty or inactive. An empty Instagram account linked from your profile does more harm than good, it suggests you started something and gave up.
            </p>

            <h2>Choosing Your Template</h2>

            <p>
                KonarCard offers five template designs. The right choice depends on your trade and personal preference, there is no universally correct answer.
            </p>

            <p>
                Look at each template with your own content in mind rather than the placeholder images. Some templates show work photos more prominently. Some give more space to text and service descriptions. Try the one that feels most like the professional impression you want to make and change it any time if you decide something else suits you better.
            </p>

            <p>
                The template is the presentation. The content is what sells. A well-filled profile on any template will outperform a poorly filled profile on the most visually striking template every time.
            </p>

            <h2>Completing Your Profile Before You Share It</h2>

            <p>
                Before you tap your card to your first customer, run through this checklist:
            </p>

            <ul>
                <li><strong>Profile photo</strong>, a real photo of your face, clear and well lit.</li>
                <li><strong>Cover image</strong>, a photo of your best work or your branded van.</li>
                <li><strong>Business name and trade title</strong>, specific and consistent with how you appear elsewhere.</li>
                <li><strong>Location and service area</strong>, clear about where you work.</li>
                <li><strong>Services</strong>, specific list covering everything you offer, with prices where possible.</li>
                <li><strong>Work photos</strong>, at least six photos of completed jobs across a range of types.</li>
                <li><strong>Contact details</strong>, phone number, email and any other relevant contact methods.</li>
                <li><strong>Social links</strong>, only if the accounts are active and show your work.</li>
                <li><strong>Template</strong>, chosen and previewed with your real content in place.</li>
            </ul>

            <p>
                A profile that ticks every point on that list is doing genuine selling work every time someone views it. It takes about thirty minutes to complete properly and once it is done it requires very little ongoing maintenance, just adding new photos as you complete jobs and updating your services and prices if they change.
            </p>
        </BlogPostLayout>
    );
}
